import { Audience } from 'ability'
import { ChatSchema, ChatsPublicSchema, VoiceSessionSchema } from 'models'
import type {
    AgentAccess,
    Chat,
    DeviceContext,
    MessageFeedback,
    AgentPublic,
    MediaSource,
    VoiceSession,
} from 'models'
import { addInitialSlash, removeTrailingSlash } from 'utils'
import { resolveShortUrls as resolveShortUrlsRequest } from './resolveShortUrls'

// The chats endpoints respond with the full shape (frontoffice) or the
// trimmed public one — same union the backend declares. Parsing coerces the
// date fields (plain JSON strings otherwise); on mismatch keep the raw
// payload instead of failing.
const ConversationSchema = ChatSchema.or(ChatsPublicSchema)
function parseConversation(raw: unknown): Chat {
    const result = ConversationSchema.safeParse(raw)
    return (result.success ? result.data : raw) as Chat
}

/**
 * What went wrong, in words, from the problem+json the backend answers with
 * (`{ type, title, status, message }`). Falling straight back to the status
 * produced "API error: 409" on screen, which tells the reader nothing and hides
 * a message that was already there and already explains the fix.
 */
async function readErrorMessage(response: Response): Promise<string> {
    const fallback = `${response.status} ${response.statusText}`.trim()
    try {
        const body = (await response.json()) as {
            message?: string
            title?: string
        }
        return body.message?.trim() || body.title?.trim() || fallback
    } catch {
        return fallback
    }
}

export interface ChatbotApiClientOptions {
    baseUrl: string
    basePath?: string
    headers?: () => Record<string, string> | undefined
    credentials?: RequestCredentials
}

export interface SubmitFeedbackPayload {
    messageId: string
    chatId: string
    agentId: string
    vote?: 'upvote' | 'downvote'
    userQuestion: string
    assistantMessage: string
    comment?: string
}

export interface SubmitLeadPayload {
    agentId: string
    chatId: string
    [key: string]: unknown
}

/** A file from the user's "My files" KB, as listed for a chat. */
export interface ChatbotFile {
    documentId: string
    name: string | null
    mimeType: string | null
    indexStatus: string
    createdAt: string
}

export interface ChatbotApiClient {
    getConversations(
        agentId: string,
        userId: string | undefined,
    ): Promise<Chat[]>
    getChat(agentId: string, chatId: string): Promise<Chat | null>
    updateChatTitle(
        agentId: string,
        chatId: string,
        title: string,
    ): Promise<Chat>
    deleteChat(agentId: string, chatId: string): Promise<void>
    getMessageFeedbacks(chatId: string): Promise<MessageFeedback[]>
    submitMessageFeedback(
        payload: SubmitFeedbackPayload,
    ): Promise<MessageFeedback>
    submitLead(payload: SubmitLeadPayload): Promise<void>
    getAgent(orgSlug: string, agentId: string): Promise<AgentPublic>
    getAgentAccess(orgSlug: string, agentId: string): Promise<AgentAccess>
    getMediaUrl(agentId: string, chatId: string, storedFileName: string): string
    appendAuthToken(url: string): string
    uploadMedia(
        agentId: string,
        chatId: string,
        file: File,
    ): Promise<MediaSource>
    getChatEndpoint(agentId: string, type?: 'chat' | 'reasoning'): string
    expandSourceContext(
        agentId: string,
        documentId: string,
        chunkIndex: number,
        windowSize?: number,
    ): Promise<{ content: string }>
    downloadSourceDocument(
        agentId: string,
        documentId: string,
    ): Promise<{ downloadUrl: string; filename: string; mimeType: string }>
    reverseGeocode(
        lat: number,
        lon: number,
        lang?: string,
    ): Promise<string | undefined>
    forwardGeocode(
        query: string,
        lang?: string,
    ): Promise<
        { latitude: number; longitude: number; displayName: string } | undefined
    >
    getOAuthAuthorizeUrl(
        agentId: string,
        serverName: string,
    ): Promise<{ mcpServerId: string; authorizeUrl: string }>
    getOAuthConnectionStatus(
        agentId: string,
        serverName: string,
    ): Promise<{ mcpServerId: string; connected: boolean }>
    resolveShortUrls(hashes: string[]): Promise<Record<string, string>>
    getChatFiles(chatId: string): Promise<ChatbotFile[]>
    getChatFileDownloadUrl(documentId: string, inline?: boolean): string
    getChatFilePreviewUrl(documentId: string): string
    deleteChatFile(documentId: string): Promise<void>
    createVoiceSession(
        agentId: string,
        chatId?: string,
        /**
         * Everything the call inherits from the page it starts on. Sent once,
         * because the spoken turns arrive from the voice provider and carry
         * none of it.
         */
        context?: {
            deviceContext?: DeviceContext
            externalContext?: Record<string, unknown>
        },
    ): Promise<VoiceSession>
    closeVoiceSession(agentId: string, sessionId: string): Promise<void>
}

export function createChatbotApiClient(
    options: ChatbotApiClientOptions,
): ChatbotApiClient {
    const basePath = options.basePath
        ? addInitialSlash(options.basePath)
        : `/api/v1/${Audience.Public}`

    const baseUrl = `${removeTrailingSlash(options.baseUrl)}${basePath}`

    // "My files" lives under the frontoffice audience, not the agent's base
    // (which may be public, backoffice, …) — derive it by swapping the trailing
    // audience segment of basePath for `frontoffice`.
    const frontofficeBase = `${removeTrailingSlash(options.baseUrl)}${basePath.replace(
        /\/[^/]+$/,
        `/${Audience.Frontoffice}`,
    )}`

    async function request<T>(
        url: string,
        init?: RequestInit,
        responseType: 'json' | 'void' = 'json',
    ): Promise<T> {
        const response = await fetch(`${baseUrl}${url}`, {
            credentials: options.credentials ?? 'include',
            ...init,
            headers: {
                ...options.headers?.(),
                ...init?.headers,
            },
        })

        if (!response.ok) {
            throw new Error(await readErrorMessage(response))
        }

        if (responseType === 'void' || response.status === 204) {
            return undefined as T
        }

        return response.json() as Promise<T>
    }

    return {
        async getConversations(agentId, userId) {
            const userFilter = userId
                ? `&filter[userId]=${encodeURIComponent(userId)}`
                : ''
            const conversations = await request<unknown[]>(
                `/agents/${encodeURIComponent(agentId)}/chats?sort=-lastMessageAt&page[size]=100${userFilter}`,
            )
            return conversations.map(parseConversation)
        },

        async getChatFiles(chatId) {
            const response = await fetch(
                `${frontofficeBase}/me/documents?filter[chatId]=${encodeURIComponent(chatId)}&page[size]=100`,
                {
                    credentials: options.credentials ?? 'include',
                    headers: { ...options.headers?.() },
                },
            )
            if (!response.ok) {
                throw new Error(
                    `API error: ${response.status} ${response.statusText}`,
                )
            }
            return (await response.json()) as ChatbotFile[]
        },

        getChatFileDownloadUrl(documentId, inline) {
            const suffix = inline ? '?inline=true' : ''
            // appendAuthToken carries a bearer in the query so <img>/<a>
            // subresources (which can't set an Authorization header) authorize
            // cross-origin in the embedded widget; no-op for cookie auth.
            return this.appendAuthToken(
                `${frontofficeBase}/me/documents/${encodeURIComponent(documentId)}/download${suffix}`,
            )
        },

        getChatFilePreviewUrl(documentId) {
            // Small resized preview blob for row thumbnails (see download
            // endpoint ?preview=true); auth token carried for cross-origin <img>.
            return this.appendAuthToken(
                `${frontofficeBase}/me/documents/${encodeURIComponent(documentId)}/download?preview=true`,
            )
        },

        async deleteChatFile(documentId) {
            const response = await fetch(
                `${frontofficeBase}/me/documents/${encodeURIComponent(documentId)}`,
                {
                    method: 'DELETE',
                    credentials: options.credentials ?? 'include',
                    headers: { ...options.headers?.() },
                },
            )
            if (!response.ok) {
                throw new Error(
                    `API error: ${response.status} ${response.statusText}`,
                )
            }
        },

        async getChat(agentId, chatId) {
            const result = await request<Chat | undefined>(
                `/agents/${encodeURIComponent(agentId)}/chats/${encodeURIComponent(chatId)}`,
            )
            return result ? parseConversation(result) : null
        },

        async updateChatTitle(agentId, chatId, title) {
            return parseConversation(
                await request<Chat>(
                    `/agents/${encodeURIComponent(agentId)}/chats/${encodeURIComponent(chatId)}`,
                    {
                        method: 'PATCH',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ title }),
                    },
                ),
            )
        },

        async deleteChat(agentId, chatId) {
            await request<void>(
                `/agents/${encodeURIComponent(agentId)}/chats/${encodeURIComponent(chatId)}`,
                { method: 'DELETE' },
                'void',
            )
        },

        async getMessageFeedbacks(chatId) {
            return request<MessageFeedback[]>(
                `/message-feedbacks?filter[chatId]=${encodeURIComponent(chatId)}&page[size]=200`,
            )
        },

        async submitMessageFeedback(payload) {
            return request<MessageFeedback>('/message-feedbacks', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            })
        },

        async submitLead(payload) {
            await request<void>(
                '/leads',
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                },
                'void',
            )
        },

        async getAgent(orgSlug, agentId) {
            return request<AgentPublic>(
                `/organizations/${encodeURIComponent(orgSlug)}/agents/${encodeURIComponent(agentId)}`,
            )
        },

        async getAgentAccess(orgSlug, agentId) {
            return request<AgentAccess>(
                `/organizations/${encodeURIComponent(orgSlug)}/agents/${encodeURIComponent(agentId)}/access`,
            )
        },

        async uploadMedia(agentId, chatId, file) {
            const formData = new FormData()
            formData.append('file', file)
            const response = await fetch(
                `${baseUrl}/agents/${encodeURIComponent(agentId)}/chats/${encodeURIComponent(chatId)}/media/upload`,
                {
                    method: 'POST',
                    credentials: options.credentials ?? 'include',
                    // Do NOT set Content-Type — browser sets multipart/form-data with boundary automatically
                    headers: options.headers?.(),
                    body: formData,
                },
            )
            if (!response.ok) {
                const text = await response.text().catch(() => '')
                let title = `Upload failed: ${response.status}`
                try {
                    title =
                        (JSON.parse(text) as { title?: string }).title ?? title
                } catch {
                    // ignore parse errors
                }
                throw new Error(title)
            }
            return response.json() as Promise<MediaSource>
        },

        getChatEndpoint(agentId, type = 'chat') {
            const path = type === 'reasoning' ? 'reasoning-chat' : 'chat'
            return `${baseUrl}/agents/${encodeURIComponent(agentId)}/${path}`
        },

        appendAuthToken(url) {
            const bearer = options
                .headers?.()
                ?.[`Authorization`]?.slice('Bearer '.length)
            if (!bearer) {
                return url
            }
            const separator = url.includes('?') ? '&' : '?'
            return `${url}${separator}token=${encodeURIComponent(bearer)}`
        },

        getMediaUrl(agentId, chatId, storedFileName) {
            const base = `${baseUrl}/agents/${encodeURIComponent(agentId)}/chats/${encodeURIComponent(chatId)}/media/${encodeURIComponent(storedFileName)}/view`
            return this.appendAuthToken(base)
        },

        async expandSourceContext(
            agentId,
            documentId,
            chunkIndex,
            windowSize = 2,
        ) {
            return request<{ content: string }>(
                `/agents/${encodeURIComponent(agentId)}/sources/expand-context`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        documentId,
                        chunkIndex,
                        windowSize,
                    }),
                },
            )
        },

        async downloadSourceDocument(agentId, documentId) {
            return request<{
                downloadUrl: string
                filename: string
                mimeType: string
            }>(
                `/agents/${encodeURIComponent(agentId)}/sources/documents/${encodeURIComponent(documentId)}/download`,
            )
        },

        async reverseGeocode(lat, lon, lang) {
            const params = new URLSearchParams({
                lat: String(lat),
                lon: String(lon),
            })
            if (lang) {
                params.set('lang', lang)
            }
            try {
                const data = await request<{ displayName?: string }>(
                    `/geocoder/reverse?${params}`,
                )
                return data.displayName
            } catch {
                return undefined
            }
        },

        async forwardGeocode(query, lang) {
            const params = new URLSearchParams({ q: query })
            if (lang) {
                params.set('lang', lang)
            }
            try {
                return await request<{
                    latitude: number
                    longitude: number
                    displayName: string
                }>(`/geocoder/forward?${params}`)
            } catch {
                return undefined
            }
        },

        async getOAuthAuthorizeUrl(agentId, serverName) {
            const params = new URLSearchParams({ serverName })
            return request<{ mcpServerId: string; authorizeUrl: string }>(
                `/agents/${encodeURIComponent(agentId)}/mcp-oauth/authorize?${params}`,
            )
        },

        async getOAuthConnectionStatus(agentId, serverName) {
            const params = new URLSearchParams({ serverName })
            return request<{ mcpServerId: string; connected: boolean }>(
                `/agents/${encodeURIComponent(agentId)}/mcp-oauth/status?${params}`,
            )
        },

        /**
         * Opens a voice session on the conversation currently on screen, so the
         * spoken turns land in the same thread the user can keep typing into.
         * The signed URL it returns is short-lived and per session: the provider
         * credential stays on the server.
         */
        async createVoiceSession(agentId, chatId, context) {
            return VoiceSessionSchema.parse(
                await request<unknown>(
                    `/agents/${encodeURIComponent(agentId)}/voice/session`,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            ...(chatId ? { chatId } : {}),
                            // Sent once for the whole call: the spoken turns
                            // come from the provider and carry nothing about
                            // this browser nor about the page it runs on.
                            ...(context?.deviceContext
                                ? { deviceContext: context.deviceContext }
                                : {}),
                            ...(context?.externalContext
                                ? { externalContext: context.externalContext }
                                : {}),
                        }),
                    },
                ),
            )
        },

        /**
         * `keepalive` on purpose: this is the call that settles the minutes of a
         * session, and it is often made while the page is going away (hang up on
         * unload, tab closed). Without it the browser is free to cancel it.
         */
        async closeVoiceSession(agentId, sessionId) {
            await request<void>(
                `/agents/${encodeURIComponent(agentId)}/voice/session/${encodeURIComponent(sessionId)}`,
                { method: 'DELETE', keepalive: true },
                'void',
            )
        },

        resolveShortUrls(hashes) {
            return resolveShortUrlsRequest(options.baseUrl, hashes, {
                headers: options.headers?.(),
                credentials: options.credentials,
            })
        },
    }
}
